import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { media } from '../../styles/breakpoints';
import { font } from '../../styles/typography';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: var(--spark-color-backdrop);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.15s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Dialog = styled.div`
  background: var(--spark-color-surface);
  border-radius: 12px;
  width: ${(p) => (p.$size === 'xl' ? '90vw' : p.$size === 'sm' ? '420px' : p.$size === 'lg' ? '800px' : '600px')};
  height: ${(p) => (p.$size === 'xl' ? '90vh' : 'auto')};
  max-width: 90vw;
  max-height: ${(p) => (p.$size === 'xl' ? '90vh' : '85vh')};
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px var(--spark-color-shadow-modal);
  animation: slideUp 0.2s ease;

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  ${media.laptopSm`
    max-height: 92vh;
  `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid var(--spark-color-border);
`;

const Title = styled.h2`
  margin: 0;
  ${font('xl')}
  font-weight: 600;
  color: var(--spark-color-text);
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--spark-color-text-muted);
  padding: 4px;
  line-height: 1;
  border-radius: 4px;

  &:hover {
    background: var(--spark-color-control-hover-surface);
    color: var(--spark-color-text-control);
  }
`;

const Body = styled.div`
  padding: ${(p) => (p.$bodyPadding !== undefined ? p.$bodyPadding : '24px')};
  overflow-y: auto;
  flex: 1;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px;
  border-top: 1px solid var(--spark-color-border);
  flex-shrink: 0;
`;

/**
 * `dismissable` (типово true) — чи закривати по кліку на підкладку та Escape. Форми з введеними
 * даними передають false: випадковий клік повз діалог не губить роботу, закриття — лише хрестиком
 * або кнопкою «Скасувати» усередині.
 *
 * Рендериться порталом у document.body: модалка з формою всередині іншої форми (напр. створення
 * контрагента з форми звернення) інакше стає вкладеним <form>, який браузер відкидає, і кнопка
 * «Створити» сабмітить зовнішню форму. React прокидує події порталу по дереву компонентів, тому
 * submit додатково зупиняється на діалозі, щоб не дійти до зовнішнього onSubmit.
 */
export default function Modal({ isOpen, onClose, title, size = 'md', bodyPadding, footer, children, dismissable = true }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape' && dismissable) onClose?.();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, dismissable]);

  if (!isOpen) return null;

  return createPortal(
    <Overlay data-testid="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && dismissable) onClose?.(); }}>
      <Dialog ref={ref} $size={size} onClick={(e) => e.stopPropagation()} onSubmit={(e) => e.stopPropagation()}>
        <Header>
          <Title>{title}</Title>
          <CloseBtn onClick={onClose}>&times;</CloseBtn>
        </Header>
        <Body $bodyPadding={bodyPadding}>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Dialog>
    </Overlay>,
    document.body,
  );
}
